import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, Signal, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/internal/operators/tap';
import { isPlatformBrowser } from '@angular/common';

const SELECTED_COUNTRY_KEY = 'selectedCountry';
// Hardcoded fallback only used if backend completely fails
const HARDCODED_FALLBACK_COUNTRY = {
    _id: null,
    code: 'BHR',
    name: { en: 'Bahrain', ar: 'البحرين' },
    isDefault: true
};

@Injectable({
    providedIn: 'root'
})
export class SharedService {
    private locationSignal = signal<string>('homepage');

    public set location(loc: string) {
        if (!loc) loc = 'homepage';
        this.locationSignal.set(loc);
    }

    public get location(): Signal<string> {
        return this.locationSignal.asReadonly();
    }

    // Iframe preview mode - hides header/footer when embedded in admin
    private iframeModeSubject$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    readonly iframeMode$: Observable<boolean> = this.iframeModeSubject$.asObservable();

    public set iframeMode(value: boolean) {
        this.iframeModeSubject$.next(value);
    }

    public get iframeMode(): boolean {
        return this.iframeModeSubject$.value;
    }

    public searchDropdownSubject$: BehaviorSubject<any> = new BehaviorSubject(
        null
    );
    readonly searchDropdown$: Observable<
        any
    > = this.searchDropdownSubject$.asObservable();

    public pageSubject$: BehaviorSubject<any> = new BehaviorSubject(null);
    readonly page$: Observable<any> = this.pageSubject$.asObservable();

    public sectorSubject$: BehaviorSubject<any> = new BehaviorSubject(null);
    readonly sector$: Observable<any> = this.sectorSubject$.asObservable();

    public currentCategorySubject$: BehaviorSubject<any> = new BehaviorSubject(
        null
    );
    readonly currentCategory$: Observable<
        any
    > = this.currentCategorySubject$.asObservable();

    public countriesSubject$: BehaviorSubject<any> = new BehaviorSubject(null);
    readonly sectors$: Observable<
        any[]
    > = this.countriesSubject$.asObservable();
    public advancedSearchSubject$: BehaviorSubject<any> = new BehaviorSubject(
        null
    );
    readonly advancedSearch$: Observable<
        any
    > = this.advancedSearchSubject$.asObservable();

    public currentCountrySubject$: BehaviorSubject<any> = new BehaviorSubject(
        null
    );
    readonly currentCountry$: Observable<
        any
    > = this.currentCountrySubject$.asObservable();

    public selectedCitySubject$: BehaviorSubject<any> = new BehaviorSubject(
        null
    );
    readonly selectedCity$: Observable<
        any
    > = this.selectedCitySubject$.asObservable();

    private hideHeaderSubject$: BehaviorSubject<any> = new BehaviorSubject(
        false
    );
    readonly hideHeader$: Observable<
        any
    > = this.hideHeaderSubject$.asObservable();

    set hideHeader(hide) {
        this.hideHeaderSubject$.next(hide);
    }

    get hideHeader(): any {
        return this.hideHeader$;
    }

    constructor(
        private http: HttpClient,
        @Inject(PLATFORM_ID) private platformId: any
    ) {
        this.loadSelectedCountryFromStorage();
    }

    /**
     * Get the currently selected country
     */
    public get selectedCountry(): any {
        return this.currentCountrySubject$.value;
    }

    /**
     * Set the selected country and persist to localStorage
     * Returns true if successfully saved, false if localStorage unavailable (e.g., private mode)
     */
    public setSelectedCountry(country: any): boolean {
        if (!country) return false;

        this.currentCountrySubject$.next(country);

        if (isPlatformBrowser(this.platformId)) {
            try {
                // Test if localStorage is available (private/incognito mode may block it)
                localStorage.setItem(
                    SELECTED_COUNTRY_KEY,
                    JSON.stringify(country)
                );
                return true;
            } catch (error) {
                console.warn(
                    '[SharedService] localStorage unavailable (private mode?):',
                    error
                );
                // In private/incognito mode, localStorage fails but memory state still works
                return false;
            }
        }
        return false;
    }

    /**
     * Load selected country from localStorage on initialization
     * Falls back to hardcoded default only if localStorage unavailable (private/incognito mode)
     * The proper default from backend will be set when countries are loaded
     */
    private loadSelectedCountryFromStorage(): void {
        if (isPlatformBrowser(this.platformId)) {
            try {
                const savedCountry = localStorage.getItem(SELECTED_COUNTRY_KEY);
                if (savedCountry) {
                    const country = JSON.parse(savedCountry);
                    this.currentCountrySubject$.next(country);
                } else {
                    // No saved country - will be set from backend when countries load
                    // Use hardcoded fallback temporarily
                    this.currentCountrySubject$.next(
                        HARDCODED_FALLBACK_COUNTRY
                    );
                }
            } catch (error) {
                console.warn(
                    '[SharedService] localStorage unavailable (private mode?), using fallback:',
                    error
                );
                // In private/incognito mode, use hardcoded fallback temporarily
                // Will be replaced with backend default when countries load
                this.currentCountrySubject$.next(HARDCODED_FALLBACK_COUNTRY);
            }
        } else {
            // Server-side rendering - use hardcoded fallback
            this.currentCountrySubject$.next(HARDCODED_FALLBACK_COUNTRY);
        }
    }

    /**
     * Initialize country selection with available countries from backend
     * 
     * PRIORITY ORDER:
     * 1. LocalStorage (user's explicit selection) - HIGHEST PRIORITY
     * 2. Backend session (for new users or when localStorage unavailable)
     * 3. Backend default (isDefault flag)
     * 4. First country (fallback)
     * 
     * This order ensures:
     * - User selections persist across page reloads (via localStorage)
     * - New users get backend default (via backend session)
     * - Private/incognito mode works (localStorage blocked → falls back to backend session)
     * - Logged-in users get their saved preference (loaded into backend session)
     *
     * Edge cases handled:
     * 1. No countries from backend - use hardcoded fallback
     * 2. No default marked in backend - use first country
     * 3. LocalStorage country not in backend list - fall back to backend session
     * 4. Backend session country not in list - use backend default
     * 5. Hardcoded fallback country - replace with backend country
     * 
     * CRITICAL FIX: Backend session country now takes priority over localStorage
     * This ensures logged-in users always see their saved preference, and
     * prevents localStorage from overriding the backend's authoritative state.
     */
    public initializeCountrySelection(
        countries: any[],
        backendSelectedCountryId?: string
    ): void {
        const currentCountry = this.currentCountrySubject$.value;

        // Edge case 1: No countries from backend
        if (!countries || countries.length === 0) {
            console.warn(
                '[SharedService] No countries available from backend, using hardcoded fallback'
            );
            this.setSelectedCountry(HARDCODED_FALLBACK_COUNTRY);
            return;
        }

        // CRITICAL FIX: Backend session now takes PRIORITY over localStorage
        // This is because:
        // 1. Backend session is the authoritative source of truth
        // 2. On login, backend restores user's saved country preference to session
        // 3. On logout, backend preserves the country in the new guest session
        // 4. LocalStorage can become stale or inconsistent
        
        // PRIORITY 1: Backend session (authoritative source)
        if (backendSelectedCountryId) {
            const backendCountry = countries.find(
                c => c._id === backendSelectedCountryId
            );
            if (backendCountry) {
                // Update both memory and localStorage to match backend
                this.setSelectedCountry(backendCountry);
                return;
            } else {
                console.warn(
                    '[SharedService] ⚠ Backend country not found in list:',
                    backendSelectedCountryId
                );
                // Continue to fallback options
            }
        }

        // PRIORITY 2: LocalStorage (only if no backend session country)
        // This handles the case where backend session expired but localStorage persists
        if (
            currentCountry &&
            currentCountry._id !== null &&
            currentCountry.code !== HARDCODED_FALLBACK_COUNTRY.code
        ) {
            const savedCountryExists = countries.find(
                c => c._id === currentCountry._id
            );
            if (savedCountryExists) {
                // Update with fresh data from backend
                this.setSelectedCountry(savedCountryExists);
                return;
            } else {
                console.warn(
                    '[SharedService] ⚠ localStorage country no longer exists:',
                    currentCountry.code
                );
                // Continue to fallback options
            }
        }

        // PRIORITY 3: Backend default country
        const backendDefaultCountry = countries.find(c => c.isDefault === true);
        const defaultCountry = backendDefaultCountry || countries[0];

        if (!backendDefaultCountry) {
            console.warn(
                '[SharedService] No default country marked in backend, using first country:',
                defaultCountry.code
            );
        }

        this.setSelectedCountry(defaultCountry);
    }

    get advancedSearch(): any {
        return this.advancedSearch$;
    }

    get page(): any {
        return this.pageSubject$.value != 'homepage'
            ? this.pageSubject$.value
            : null;
    }

    get sector(): any {
        return this.sectorSubject$.value;
    }

    get searchDropdown(): any {
        return this.searchDropdown$;
    }

    getCountries(): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/countries`).pipe(
            tap(response => {
                this.countriesSubject$.next(response);
            })
        );
    }

    public paginations = {
        limit: 24
    };

    static paginations = {
        limit: 24
    };
}
